/**
 * 草稿表格里那点公式的求值器。
 *
 * 为什么手写而不是引库：操作员实际用到的只有 =SUM(A1:A8)、=A1&"."&A2 这一类，
 * 一个完整电子表格引擎要为用不到的几百个函数付出体积与行为差异。这里刻意做成
 * "递归下降 + 明确报错"，而不是正则替换 —— 正则版本在 =SUM(A1:A3)/2 这种嵌套上
 * 会算错，而算错的表格比没有表格更坏。
 *
 * 三条容易踩错、已用测试钉住的语义：
 *   1. 引用另一个格子时必须在**新的解析上下文**里求值，否则会踩坏外层公式的读位；
 *   2. A1:A3 作为函数参数要展开成整段值，不能只取第一格；
 *   3. "公式有多余部分"只能在整条公式读完后判，在函数参数中间判会误杀。
 *
 * 错误串沿用 WPS/Excel 的写法（#DIV/0!、#REF!、#NAME?、#CIRCULAR!）。
 */

export type Cells = string[][]

export interface EvalResult {
  /** 单元格里最终显示的文字 */
  text: string
  /** 出错时给出原因，供公式条提示；正常为 undefined */
  error?: string
}

const MAX_DEPTH = 64

export function colName(idx: number): string {
  let s = ''
  let n = idx
  do {
    s = String.fromCharCode(65 + (n % 26)) + s
    n = Math.floor(n / 26) - 1
  } while (n >= 0)
  return s
}

export function cellLabel(r: number, c: number): string {
  return colName(c) + (r + 1)
}

/** "B3" -> {r:2,c:1}；不合法返回 null。 */
function parseRef(s: string): { r: number; c: number } | null {
  const m = /^([A-Z]+)([1-9][0-9]*)$/.exec(s)
  if (!m) return null
  let c = 0
  for (const ch of m[1]) c = c * 26 + (ch.charCodeAt(0) - 64)
  return { r: parseInt(m[2], 10) - 1, c: c - 1 }
}

type Val = number | string

type Tok = { k: 'num' | 'str' | 'ident' | 'op'; v: string }

function tokenize(src: string): Tok[] | string {
  const out: Tok[] = []
  let i = 0
  while (i < src.length) {
    const ch = src[i]
    if (ch === ' ' || ch === '\t') {
      i++
      continue
    }
    if (ch === '"') {
      let j = i + 1
      let s = ''
      while (j < src.length && src[j] !== '"') {
        s += src[j]
        j++
      }
      if (j >= src.length) return '公式里的字符串少了收尾的双引号'
      out.push({ k: 'str', v: s })
      i = j + 1
      continue
    }
    if (/[0-9]/.test(ch) || (ch === '.' && /[0-9]/.test(src[i + 1] || ''))) {
      let j = i
      while (j < src.length && /[0-9.]/.test(src[j])) j++
      out.push({ k: 'num', v: src.slice(i, j) })
      i = j
      continue
    }
    if (/[A-Za-z]/.test(ch)) {
      let j = i
      while (j < src.length && /[A-Za-z0-9_]/.test(src[j])) j++
      out.push({ k: 'ident', v: src.slice(i, j).toUpperCase() })
      i = j
      continue
    }
    if ('+-*/&(),:'.includes(ch)) {
      out.push({ k: 'op', v: ch })
      i++
      continue
    }
    return '公式里有认不出的字符：' + ch
  }
  return out
}

class EvalError extends Error {}

interface Ctx {
  cells: Cells
  toks: Tok[]
  pos: number
  /** 求值链上的格子，用于循环引用检测 */
  stack: Set<string>
  depth: number
}

function peek(t: Ctx): Tok | undefined {
  return t.toks[t.pos]
}

function at(t: Ctx, off: number): Tok | undefined {
  return t.toks[t.pos + off]
}

function eat(t: Ctx, v: string): boolean {
  const tk = peek(t)
  if (tk && tk.k === 'op' && tk.v === v) {
    t.pos++
    return true
  }
  return false
}

function numOf(v: Val): number {
  if (typeof v === 'number') return v
  const s = v.trim().replace(/,/g, '')
  if (s === '') return 0
  const n = Number(s)
  return Number.isFinite(n) ? n : 0
}

function strOf(v: Val): string {
  return typeof v === 'number' ? String(v) : v
}

/** 只有数字参与统计：文本格不计入，与 WPS 的 SUM/COUNT 一致。 */
function numbersOf(vals: Val[]): number[] {
  const out: number[] = []
  for (const v of vals) {
    if (typeof v === 'number') {
      out.push(v)
      continue
    }
    const s = v.trim().replace(/,/g, '')
    if (s !== '' && Number.isFinite(Number(s))) out.push(Number(s))
  }
  return out
}

function cellValue(ctx: Ctx, r: number, c: number): Val {
  const key = r + ',' + c
  if (ctx.stack.has(key)) throw new EvalError('#CIRCULAR!')
  const raw = ctx.cells[r]?.[c] ?? ''
  if (!raw.startsWith('=')) return raw
  if (ctx.depth >= MAX_DEPTH) throw new EvalError('#CIRCULAR!')
  // 关键：嵌套求值用自己的 toks/pos，只共享 cells、循环链和深度。
  return evalTop(ctx.cells, raw.slice(1), ctx.stack, ctx.depth + 1)
}

function collectRange(ctx: Ctx, a: { r: number; c: number }, b: { r: number; c: number }): Val[] {
  const r0 = Math.min(a.r, b.r)
  const r1 = Math.max(a.r, b.r)
  const c0 = Math.min(a.c, b.c)
  const c1 = Math.max(a.c, b.c)
  if (r0 < 0 || c0 < 0 || r1 > 5000 || c1 > 200) throw new EvalError('#REF!')
  const out: Val[] = []
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) out.push(cellValue(ctx, r, c))
  }
  return out
}

/** 函数位置上的一个参数：可能是区间（展开成多值），也可能是普通算式。 */
function readArg(ctx: Ctx): Val[] {
  const tk = peek(ctx)
  if (tk && tk.k === 'ident') {
    const ref = parseRef(tk.v)
    const nxt = at(ctx, 1)
    if (ref && nxt && nxt.k === 'op' && nxt.v === ':') {
      const end = at(ctx, 2)
      if (!end || end.k !== 'ident') throw new EvalError('#REF!')
      const b = parseRef(end.v)
      if (!b) throw new EvalError('#REF!')
      ctx.pos += 3
      return collectRange(ctx, ref, b)
    }
  }
  return [concatExpr(ctx)]
}

function callFunc(ctx: Ctx, name: string): Val {
  if (!eat(ctx, '(')) throw new EvalError('#NAME?')
  const args: Val[] = []
  if (!eat(ctx, ')')) {
    do {
      args.push(...readArg(ctx))
    } while (eat(ctx, ','))
    if (!eat(ctx, ')')) throw new EvalError('公式少了右括号')
  }
  const nums = numbersOf(args)
  switch (name) {
    case 'SUM':
      return nums.reduce((a, b) => a + b, 0)
    case 'COUNT':
      return nums.length
    case 'AVERAGE':
      if (nums.length === 0) throw new EvalError('#DIV/0!')
      return nums.reduce((a, b) => a + b, 0) / nums.length
    case 'MIN':
      return nums.length ? Math.min(...nums) : 0
    case 'MAX':
      return nums.length ? Math.max(...nums) : 0
    case 'ABS':
      return Math.abs(numOf(args[0] ?? 0))
    case 'ROUND': {
      const dp = Math.trunc(numOf(args[1] ?? 0))
      const f = Math.pow(10, dp)
      return Math.round(numOf(args[0] ?? 0) * f) / f
    }
    case 'LEN':
      return strOf(args[0] ?? '').length
    case 'TRIM':
      return strOf(args[0] ?? '').trim().replace(/\s+/g, ' ')
    case 'UPPER':
      return strOf(args[0] ?? '').toUpperCase()
    case 'LOWER':
      return strOf(args[0] ?? '').toLowerCase()
    default:
      throw new EvalError('#NAME?')
  }
}

function primary(ctx: Ctx): Val {
  const tk = peek(ctx)
  if (!tk) throw new EvalError('公式不完整')
  if (tk.k === 'num') {
    ctx.pos++
    return Number(tk.v)
  }
  if (tk.k === 'str') {
    ctx.pos++
    return tk.v
  }
  if (tk.k === 'op' && tk.v === '(') {
    ctx.pos++
    const v = concatExpr(ctx)
    if (!eat(ctx, ')')) throw new EvalError('公式少了右括号')
    return v
  }
  if (tk.k === 'ident') {
    ctx.pos++
    const nxt = peek(ctx)
    if (nxt && nxt.k === 'op' && nxt.v === '(') return callFunc(ctx, tk.v)
    const ref = parseRef(tk.v)
    if (!ref) throw new EvalError('#NAME?')
    if (eat(ctx, ':')) {
      const end = peek(ctx)
      if (!end || end.k !== 'ident') throw new EvalError('#REF!')
      ctx.pos++
      const b = parseRef(end.v)
      if (!b) throw new EvalError('#REF!')
      const vals = collectRange(ctx, ref, b)
      // 裸区间出现在算式里时按第一格处理（函数参数走 readArg，不会到这里）。
      return vals.length ? vals[0] : ''
    }
    return cellValue(ctx, ref.r, ref.c)
  }
  throw new EvalError('#NAME?')
}

function unaryExpr(ctx: Ctx): Val {
  if (eat(ctx, '-')) return -numOf(unaryExpr(ctx))
  if (eat(ctx, '+')) return unaryExpr(ctx)
  return primary(ctx)
}

function mulDivExpr(ctx: Ctx): Val {
  let left = unaryExpr(ctx)
  for (;;) {
    if (eat(ctx, '*')) left = numOf(left) * numOf(unaryExpr(ctx))
    else if (eat(ctx, '/')) {
      const d = numOf(unaryExpr(ctx))
      if (d === 0) throw new EvalError('#DIV/0!')
      left = numOf(left) / d
    } else return left
  }
}

function addSubExpr(ctx: Ctx): Val {
  let left = mulDivExpr(ctx)
  for (;;) {
    if (eat(ctx, '+')) left = numOf(left) + numOf(mulDivExpr(ctx))
    else if (eat(ctx, '-')) left = numOf(left) - numOf(mulDivExpr(ctx))
    else return left
  }
}

function concatExpr(ctx: Ctx): Val {
  let left = addSubExpr(ctx)
  while (eat(ctx, '&')) left = strOf(left) + strOf(addSubExpr(ctx))
  return left
}

function evalTop(cells: Cells, src: string, stack: Set<string>, depth: number): Val {
  const toks = tokenize(src)
  if (typeof toks === 'string') throw new EvalError(toks)
  const ctx: Ctx = { cells, toks, pos: 0, stack, depth }
  const v = concatExpr(ctx)
  // 只有整条公式读完了才判"有多余部分"。
  if (ctx.pos < ctx.toks.length) throw new EvalError('公式有多余的部分')
  return v
}

function render(v: Val): string {
  if (typeof v !== 'number') return v
  // 浮点尾巴（0.1+0.2=0.30000000000000004）在表格里最刺眼，按 12 位有效数字收。
  if (Number.isInteger(v)) return String(v)
  return String(Number(v.toPrecision(12)))
}

/** 求值一个单元格；raw 不以 = 开头时原样返回。 */
export function evaluateCell(cells: Cells, r: number, c: number): EvalResult {
  const raw = cells[r]?.[c] ?? ''
  if (!raw.startsWith('=')) return { text: raw }
  try {
    return { text: render(evalTop(cells, raw.slice(1), new Set([r + ',' + c]), 0)) }
  } catch (e) {
    if (e instanceof EvalError) return { text: e.message, error: e.message }
    return { text: '#ERROR!', error: String(e) }
  }
}
